import { Stack } from '@mui/material';
import { t } from 'i18next';
import { FormEventHandler, useState } from 'react';
import { PasswordInput } from '../../common/components/inputs/PasswordInput';
import { useFormItem } from '../../common/hooks/use-form-item/index';
import { InputType } from '../../common/hooks/use-form-item/types';
import { and } from '../../common/hooks/use-form-item/validators/compositions';
import { required } from '../../common/hooks/use-form-item/validators/existential';
import { maxLength } from '../../common/hooks/use-form-item/validators/operational';
import { UserLevel, getUserLevelLabel } from '../../organization/users/enums/UserLevel';
import { UserRole } from '../../organization/users/enums/UserRole';
import { ButtonBox, PrimaryButton, TertiaryButton } from './Buttons';
import { RolesAutocomplete } from './RolesAutocomplete';

type Props = {
  useRoles: boolean;
  submit: {
    label: string;
    action: (name: string, preferredName: string | null, level: UserLevel | null, roles: UserRole[] | null, password: string) => Promise<void>;
  };
  back: {
    label: string;
    action: () => void;
  };
};

export const CreateUserForm = ({ useRoles, submit, back }: Props) => {
  const [loading, setLoading] = useState<boolean>(false);

  const [name, nameComponent, nameValid] = useFormItem<string>({
    type: InputType.TYPE_TEXT,
    label: t('createUserForm.name.input'),
    value: '',
    validator: and(required(), maxLength(1000)),
    inputOptions: {
      autoFocus: true,
      disabled: loading,
      focusedLabel: t('createUserForm.name.focused'),
      help: t('createUserForm.name.help'),
      showRequiredIndicator: true,
    },
    textOptions: { trimOnBlur: true },
  });

  const [preferredName, preferredNameComponent, preferredNameValid] = useFormItem<string | null>({
    type: InputType.TYPE_TEXT,
    label: t('createUserForm.preferredName.input'),
    value: '',
    validator: maxLength(1000),
    inputOptions: {
      disabled: loading,
      focusedLabel: t('createUserForm.preferredName.focused'),
    },
    textOptions: { trimOnBlur: true },
  });

  const [level, levelComponent, levelValid] = useFormItem<UserLevel | null>({
    type: InputType.TYPE_AUTOCOMPLETE,
    label: t('createUserForm.level.input'),
    value: null,
    validator: useRoles ? null : required(),
    inputOptions: {
      disabled: loading,
      focusedLabel: t('createUserForm.level.focused'),
      showRequiredIndicator: true,
    },
    autocompleteOptions: {
      readOnly: true,
      clearable: false,
      options: [UserLevel.TESTER, UserLevel.ANALYST, UserLevel.ADMIN].map((l) => ({ value: l, label: getUserLevelLabel(l) })),
    },
  });

  const [roles, setRoles] = useState<UserRole[]>([]);

  const [password, setPassword] = useState<string | null>(null);
  const [confirmPassword, setConfirmPassword] = useState<string | null>(null);

  const valuesValid = !!name && ((!useRoles && !!level) || (useRoles && roles.length > 0)) && !!password && !!confirmPassword;
  const formValid = nameValid && preferredNameValid && levelValid;
  const canSubmit = !loading && valuesValid && formValid;

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      setLoading(true);
      if (useRoles) {
        await submit.action(name, preferredName || null, null, roles, password);
      } else {
        await submit.action(name, preferredName || null, level, null, password);
      }
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={1.5} component="form" onSubmit={onSubmit}>
      {nameComponent}
      {preferredNameComponent}
      {!useRoles && levelComponent}
      {useRoles && (
        <RolesAutocomplete
          label={t('createUserForm.roles.input')}
          focusedLabel={t('createUserForm.roles.focused')}
          disabledReason={loading ? '' : null}
          values={roles}
          onChange={setRoles}
        />
      )}
      <PasswordInput
        name="new-password"
        label={t('createUserForm.password.input')}
        focusedLabel={t('createUserForm.password.focused')}
        disabled={loading}
        onChange={setPassword}
      />
      <PasswordInput
        name="confirm-password"
        label={t('createUserForm.confirmPassword.input')}
        focusedLabel={t('createUserForm.confirmPassword.focused')}
        disabled={loading}
        compareTo={password}
        onChange={setConfirmPassword}
      />
      <ButtonBox>
        <TertiaryButton disabled={loading} onClick={back.action}>
          {back.label}
        </TertiaryButton>
        <PrimaryButton disabled={!canSubmit} type="submit">
          {submit.label}
        </PrimaryButton>
      </ButtonBox>
    </Stack>
  );
};
